/**
 * countryCurrency.ts — map a country to its currency (ISO code).
 *
 * The currency converter (ItinerarySection → CurrencyConverter) defaults to
 * source = the venue's local currency (where the band is that day) and
 * target = the band's home currency. A venue may carry its own `currency`
 * (from an online lookup); when empty we fall back to this country→currency
 * map. (2026-08-11; P7 2026-08-21 — corrected orientation: venue SOURCE, band TARGET.)
 */

const COUNTRY_TO_CURRENCY: Record<string, string> = {
  // Europe
  Austria: 'EUR',
  Belgium: 'EUR',
  'Czech Republic': 'CZK',
  Czechia: 'CZK',
  Česko: 'CZK',
  Cyprus: 'EUR',
  Finland: 'EUR',
  France: 'EUR',
  Germany: 'EUR',
  Greece: 'EUR',
  Ελλάς: 'EUR',
  Ireland: 'EUR',
  Italy: 'EUR',
  Luxembourg: 'EUR',
  Malta: 'EUR',
  Netherlands: 'EUR',
  Portugal: 'EUR',
  Slovakia: 'EUR',
  Slovenia: 'EUR',
  Spain: 'EUR',
  Switzerland: 'CHF',
  'United Kingdom': 'GBP',
  UK: 'GBP',
  'Great Britain': 'GBP',
  Poland: 'PLN',
  Sweden: 'SEK',
  Norway: 'NOK',
  Denmark: 'DKK',
  Hungary: 'HUF',
  Romania: 'RON',
  Turkey: 'TRY',
  Bulgaria: 'BGN',
  Croatia: 'EUR',
  Serbia: 'RSD',
  Ukraine: 'UAH',
  // North America
  'United States': 'USD',
  USA: 'USD',
  'United States of America': 'USD',
  Canada: 'CAD',
  Mexico: 'MXN',
  // Asia
  Japan: 'JPY',
  China: 'CNY',
  Kazakhstan: 'KZT',
  India: 'INR',
  'South Korea': 'KRW',
  Korea: 'KRW',
  Thailand: 'THB',
  Singapore: 'SGD',
  // Middle East
  'United Arab Emirates': 'AED',
  UAE: 'AED',
  Israel: 'ILS',
  // Oceania
  Australia: 'AUD',
  'New Zealand': 'NZD',
};

/** Normalize a country string for lookup (lowercase, trim, strip accents). */
function norm(s: string): string {
  return (s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]/g, '');
}

const NORM_MAP: Record<string, string> = {};
for (const [country, currency] of Object.entries(COUNTRY_TO_CURRENCY)) {
  NORM_MAP[norm(country)] = currency;
}

/**
 * Resolve the currency ISO code for a venue country. Returns null when unknown.
 * Matching is accent/case/punctuation-insensitive and handles common aliases
 * (Czechia, Česko, Ελλάς, UK…).
 */
export function countryToCurrency(country?: string | null): string | null {
  if (!country) return null;
  const key = norm(country);
  return NORM_MAP[key] ?? null;
}

/**
 * Resolve the best default currency for a show, in priority order:
 *   1. the venue's own `currency` (from an online country lookup),
 *   2. the country→currency map,
 *   3. the show's existing `currency_note` (detected code),
 *   4. null (caller falls back to EUR).
 */
export function resolveShowCurrency(
  venueCurrency?: string | null,
  venueCountry?: string | null,
  currencyNote?: string | null,
): string | null {
  if (venueCurrency && /^[A-Z]{3}$/.test(venueCurrency.trim())) return venueCurrency.trim().toUpperCase();
  const byCountry = countryToCurrency(venueCountry);
  if (byCountry) return byCountry;
  if (currencyNote) {
    const m = currencyNote.match(/\b([A-Z]{3})\b/);
    if (m) return m[1].toUpperCase();
  }
  return null;
}

/**
 * Resolve the band's home currency from its country, falling back to EUR.
 * This is the default currency for expenses/deal, and the currency converter
 * TARGET (the band converts what it spends at the venue/hotel back into its
 * home money). (2026-08-21, P7 — corrected: band is the TARGET, venue SOURCE.)
 */
export function resolveBandCurrency(bandCountry?: string | null): string {
  return countryToCurrency(bandCountry) ?? 'EUR';
}
